/**
 * Business Units List Route
 *
 * Displays searchable, filterable list of business units
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useBusinessUnits } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { BusinessUnit, BusinessUnitFilter } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/business-units/')({
  component: BusinessUnitsListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { BusinessUnitsListPage };

interface BusinessUnitFilters {
  search: string;
  unitType: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

function BusinessUnitsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<BusinessUnitFilters>({
    search: '',
    unitType: '',
  });

  // Build GraphQL filter
  const graphqlFilter: BusinessUnitFilter | undefined = filters.unitType
    ? { unitType: filters.unitType }
    : undefined;

  // Fetch business units with GraphQL
  const { data, loading, error } = useBusinessUnits(graphqlFilter);

  // Extract business units from connection
  const businessUnits = data?.businessUnits?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<BusinessUnit>({
      items: businessUnits,
      page,
      pageSize,
      filterFn: (businessUnit) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = businessUnit.name.toLowerCase().includes(searchLower);
          const matchesDescription = businessUnit.description?.toLowerCase().includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.unitType && businessUnit.unitType !== filters.unitType) {
          return false;
        }
        return true;
      },
      sortValue: (businessUnit, column) => String(businessUnit[column as keyof BusinessUnit] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<BusinessUnit>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '35%',
      render: (businessUnit) => (
        <div>
          <div className="entity-list-name-cell">{businessUnit.name}</div>
          {businessUnit.description && (
            <div className="entity-list-description-cell">
              {businessUnit.description.substring(0, 60)}
              {businessUnit.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'unitType',
      label: 'Unit Type',
      sortable: true,
      width: '25%',
      render: (businessUnit) => <span>{businessUnit.unitType}</span>,
    },
    {
      key: 'lifecycleStatus',
      label: 'Lifecycle Status',
      sortable: true,
      width: '25%',
      render: (businessUnit) => <span>{businessUnit.lifecycleStatus || '—'}</span>,
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      width: '15%',
      render: (businessUnit) => <span>{businessUnit.code || '—'}</span>,
    },
  ];

  const handleRowClick = (businessUnit: BusinessUnit): void => {
    navigate({ to: `/business-units/${businessUnit.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<BusinessUnitFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/business-units', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', unitType: '' });
    void navigate({ to: '/business-units', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/business-units', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({
      to: '/business-units',
      search: { page: 1, pageSize: newPageSize },
    });
  };

  if (error) {
    return (
      <div className="business-units-list-page">
        <h1 className="forge-typography--headline4">Business Units</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load business units"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="business-units-list-page">
      <div className="business-units-header">
        <div>
          <h1 className="forge-typography--headline4">Business Units</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} business units`}
          </p>
        </div>
        <PermissionGate permission="business-unit:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/business-units/new' })}
          >
            Create Business Unit
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search business units..."
            aria-label="Search business units"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by unit type"
          value={filters.unitType}
          onChange={(e) => handleFilterChange({ unitType: e.target.value })}
        >
          <option value="">All Unit Types</option>
          <option value="division">Division</option>
          <option value="group">Group</option>
          <option value="team">Team</option>
          <option value="partner">Partner</option>
        </select>

        {(filters.search || filters.unitType) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="business-units-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={4} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(businessUnit) => businessUnit.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No business units found',
                description:
                  filters.search || filters.unitType
                    ? 'Try adjusting your filters'
                    : 'Create your first business unit to get started',
                actionLabel: 'Create Business Unit',
                onAction: () => navigate({ to: '/business-units/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
